import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { UserPlus, Trash2, KeyRound, Loader2, ShieldAlert, Mail, Send, Check } from 'lucide-react';
import { roleName } from '../config/roleNames';

const API = process.env.REACT_APP_BACKEND_URL;
const rLabel = (rt) => roleName(rt);

/**
 * InlineText — click-to-edit text cell (BOP TeamAccessPage pattern): autosave on
 * blur/Enter, Esc reverts, toast + revert on 403/409. `onSave(next)` must throw.
 */
function InlineText({ value, onSave, placeholder, testId, disabled }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  useEffect(() => { setVal(value || ''); }, [value]);
  const commit = async () => {
    const next = (val ?? '').trim();
    if (next === (value ?? '').trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(next);
      setFlash(true); setTimeout(() => setFlash(false), 1500);
    } catch (err) {
      setVal(value || '');
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); setEditing(false); }
  };
  if (disabled) return <span className="text-sm" data-testid={testId}>{value || <span className="italic opacity-60">{placeholder || '—'}</span>}</span>;
  if (!editing) return (
    <button type="button" onClick={() => setEditing(true)} data-testid={testId}
      className="text-left w-full text-sm hover:bg-white/10 rounded px-1.5 py-1 -mx-1.5 -my-1 transition-colors flex items-center gap-1">
      <span className={value ? '' : 'italic opacity-60'}>{value || placeholder || '—'}</span>
      {flash && <Check className="w-3 h-3 text-emerald-500" />}
    </button>
  );
  return (
    <div className="flex items-center gap-1">
      <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
          else if (e.key === 'Escape') { e.preventDefault(); setVal(value || ''); setEditing(false); }
        }}
        className="h-7 text-sm" data-testid={`${testId}-input`} />
      {saving && <Loader2 className="w-3 h-3 animate-spin opacity-60" />}
    </div>
  );
}

/** InlineSelect — role cell: save on change, revert + toast on error. */
function InlineSelect({ value, options, onSave, testId, disabled, tooltip }) {
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value); }, [value]);
  const change = async (next) => {
    if (next === value) return;
    setVal(next); setSaving(true);
    try { await onSave(next); }
    catch (err) { setVal(value); if (!err?.cancelled) toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  if (disabled) return (
    <span className="inline-flex items-center gap-1" title={tooltip} data-testid={testId}>
      <Badge variant="outline">{rLabel(value)}</Badge>
      <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
    </span>
  );
  return (
    <Select value={val} onValueChange={change}>
      <SelectTrigger className="h-7 w-[150px] text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((r) => <SelectItem key={r} value={r}>{rLabel(r)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

/**
 * OLMembersPanel — one shared staff-management console for the OL Members tiers
 * (corporate / league / customer). Driven entirely by the `scope` prop
 * (`corporate` | `league:<id>` | `org:<id>`). Website members (family) are
 * managed separately in MemberMgmtPanel.
 */
export default function OLMembersPanel({ scope }) {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], assignable_roles: [], admin_role: null });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [invites, setInvites] = useState([]);
  const [roleConfirm, setRoleConfirm] = useState(null);

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const th = `text-left text-[11px] uppercase tracking-wider ${muted} py-2 px-3`;

  const load = useCallback(async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/ol-members?scope=${encodeURIComponent(scope)}`, { withCredentials: true });
      setData(data);
      try {
        const inv = await axios.get(
          `${API}/api/invitations?scope=${encodeURIComponent(scope)}&status=pending`, { withCredentials: true });
        setInvites(inv.data.invitations || []);
      } catch { setInvites([]); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load members');
    } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const patchUser = async (u, body) => {
    await axios.patch(`${API}/api/ol-members/${u.id}`, { ...body, scope }, { withCredentials: true });
    await load();
  };

  // Role change is destructive → confirm before saving (OLMembersPanel never
  // changes a role silently). Returns a promise the InlineSelect awaits.
  const confirmRoleChange = (u, from, to) =>
    new Promise((resolve, reject) => setRoleConfirm({ u, from, to, resolve, reject }));
  const doRoleChange = async () => {
    const rc = roleConfirm;
    try { await patchUser(rc.u, { role: rc.to }); rc.resolve(); }
    catch (e) { rc.reject(e); }
    finally { setRoleConfirm(null); }
  };
  const cancelRoleChange = () => {
    if (roleConfirm) roleConfirm.reject({ cancelled: true });
    setRoleConfirm(null);
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.email} from this scope?`)) return;
    try {
      await axios.delete(`${API}/api/ol-members/${u.id}?scope=${encodeURIComponent(scope)}`,
        { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const resendInvite = async (id) => {
    try {
      await axios.post(`${API}/api/invitations/${id}/resend`, {}, { withCredentials: true });
      toast.success('Invite resent'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
  };
  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await axios.delete(`${API}/api/invitations/${id}`, { withCredentials: true });
      toast.success('Invite revoked'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); }
  };

  return (
    <Card className={card} data-testid="ol-members-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">OmniLeagues Members</CardTitle>
          <p className={`text-sm ${muted}`}>Staff accounts you administer in this scope.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowInvite(true)} data-testid="ol-member-invite-btn"
            className="bg-teal-600 hover:bg-teal-700 text-white">
            <Mail className="h-4 w-4 mr-1.5" /> Invite Member
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowCreate(true)} data-testid="ol-member-add-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Create with password
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ol-members-table">
              <thead><tr className="border-b border-slate-700/40">
                <th className={th}>Name</th><th className={th}>Email</th>
                <th className={th}>Phone</th><th className={th}>Role</th>
                <th className={th}>Status</th>
                <th className={`${th} text-right`}>Actions</th>
              </tr></thead>
              <tbody>
                {data.users.length === 0 && (
                  <tr><td colSpan={6} className={`py-8 text-center ${muted}`}>No members in this scope.</td></tr>
                )}
                {data.users.map((u) => {
                  const isSelf = u.id === user?.id;
                  const scopeRole = (u.roles.find(r => data.assignable_roles.includes(r.role_type)) || u.roles[0] || {}).role_type;
                  const lockDel = isSelf || u.is_last_admin;
                  const delTip = isSelf ? 'You cannot delete your own account'
                    : (u.is_last_admin ? 'Cannot remove the last active admin of this scope' : 'Remove from scope');
                  const roleLock = u.is_last_admin || (isSelf && u.is_admin);
                  const roleTip = u.is_last_admin ? 'Cannot change the role of the last active admin'
                    : (isSelf && u.is_admin ? 'You cannot remove your own admin role' : '');
                  const statusLock = isSelf || u.is_last_admin;
                  const statusTip = isSelf ? 'You cannot disable your own account'
                    : (u.is_last_admin ? 'Cannot disable the last active admin of this scope' : '');
                  return (
                    <tr key={u.id} data-testid={`ol-member-row-${u.id}`}
                      className={`border-b border-slate-700/20 ${u.disabled ? 'opacity-50' : ''}`}>
                      <td className="py-2 px-3">
                        <InlineText value={`${u.first_name || ''} ${u.last_name || ''}`.trim()}
                          placeholder="Name" testId={`ol-member-name-${u.id}`}
                          onSave={(next) => {
                            const p = next.split(/\s+/);
                            return patchUser(u, { first_name: p[0] || '', last_name: p.slice(1).join(' ') });
                          }} />
                        {isSelf && <span className={`ml-1 text-xs ${muted}`}>(you)</span>}
                      </td>
                      <td className="py-2 px-3">{u.email}</td>
                      <td className="py-2 px-3">
                        <InlineText value={u.phone || ''} placeholder="—" testId={`ol-member-phone-${u.id}`}
                          onSave={(next) => patchUser(u, { phone: next })} />
                      </td>
                      <td className="py-2 px-3" data-testid={`ol-member-role-${u.id}`}>
                        <InlineSelect value={scopeRole} options={data.assignable_roles}
                          disabled={roleLock} tooltip={roleTip} testId={`ol-member-role-select-${u.id}`}
                          onSave={(next) => confirmRoleChange(u, scopeRole, next)} />
                      </td>
                      <td className="py-2 px-3" data-testid={`ol-member-status-${u.id}`}>
                        <span className="inline-flex items-center gap-2" title={statusTip}>
                          <Switch checked={!u.disabled} disabled={statusLock}
                            data-testid={`ol-member-status-switch-${u.id}`}
                            onCheckedChange={async (checked) => {
                              try { await patchUser(u, { disabled: !checked }); }
                              catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
                            }} />
                          <span className={`text-xs ${u.disabled ? 'text-red-500' : 'text-emerald-500'}`}>
                            {u.disabled ? 'Disabled' : 'Active'}
                          </span>
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setPwUser(u)}
                          data-testid={`ol-member-setpw-${u.id}`} title="Set password">
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" disabled={lockDel} title={delTip}
                          onClick={() => del(u)} data-testid={`ol-member-del-${u.id}`}
                          className="text-red-500 disabled:opacity-30">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {invites.length > 0 && (
          <div className="mt-6" data-testid="ol-member-pending-invites">
            <h4 className={`text-sm font-semibold mb-2 ${muted}`}>Pending Invites</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-700/40">
                  <th className={th}>Email</th><th className={th}>Role</th>
                  <th className={th}>Sent</th><th className={th}>Expires</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr></thead>
                <tbody>
                  {invites.map((iv) => (
                    <tr key={iv.id} data-testid={`ol-invite-row-${iv.id}`} className="border-b border-slate-700/20">
                      <td className="py-2 px-3">{iv.email}</td>
                      <td className="py-2 px-3"><Badge variant="outline">{iv.role_label || rLabel(iv.role_type)}</Badge></td>
                      <td className="py-2 px-3">{(iv.created_at || '').slice(0, 10)}</td>
                      <td className="py-2 px-3">{(iv.expires_at || '').slice(0, 10)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" title="Resend" data-testid={`ol-invite-resend-${iv.id}`}
                          onClick={() => resendInvite(iv.id)}><Send className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" title="Revoke" className="text-red-500"
                          data-testid={`ol-invite-revoke-${iv.id}`} onClick={() => revokeInvite(iv.id)}>
                          <Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>

      {showInvite && (
        <InviteDialog scope={scope} roles={data.assignable_roles}
          onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); load(); }} />
      )}
      {showCreate && (
        <AddDialog scope={scope} roles={data.assignable_roles}
          onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />
      )}
      {pwUser && (
        <PwDialog scope={scope} u={pwUser}
          onClose={() => setPwUser(null)} onSaved={() => setPwUser(null)} />
      )}
      {roleConfirm && (
        <Dialog open onOpenChange={(v) => !v && cancelRoleChange()}>
          <DialogContent>
            <DialogHeader><DialogTitle>Change role?</DialogTitle>
              <DialogDescription>
                Change {roleConfirm.u.first_name} {roleConfirm.u.last_name} from
                {' '}<strong>{rLabel(roleConfirm.from)}</strong> to <strong>{rLabel(roleConfirm.to)}</strong>?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={cancelRoleChange} data-testid="ol-role-change-cancel">Cancel</Button>
              <Button onClick={doRoleChange} data-testid="ol-role-change-confirm"
                className="bg-teal-600 hover:bg-teal-700 text-white">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function InviteDialog({ scope, roles, onClose, onSaved }) {
  const isOrg = scope.startsWith('org:');
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '', role: '' });
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [mode, setMode] = useState(isOrg ? 'pick' : 'new');
  const [search, setSearch] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((f.email || '').trim());
  const canPick = (c) => !c.has_ol_login && !c.is_website_member;  // no-email rows ARE selectable

  useEffect(() => {
    if (!isOrg) return;
    axios.get(`${API}/api/ol-members/staff-candidates?scope=${encodeURIComponent(scope)}`, { withCredentials: true })
      .then(({ data }) => setCandidates(data.candidates || []))
      .catch(() => { setCandidates([]); setMode('new'); });
  }, [scope, isOrg]);

  const choosePerson = (c) => {
    setF((p) => ({ ...p, first_name: c.first_name, last_name: c.last_name, email: c.email, phone: c.phone }));
    setMode('new');
  };
  const chooseNew = () => {
    setF({ first_name: '', last_name: '', email: '', phone: '', role: '' });
    setMode('new');
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/invitations`,
        { scope, role_type: f.role, email: f.email, first_name: f.first_name, last_name: f.last_name, phone: f.phone },
        { withCredentials: true });
      toast.success(data.email_sent === false ? 'Invite created (email not sent)' : 'Invitation emailed');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invite failed'); }
    finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = candidates.filter((c) => !q
    || `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(q)
    || (c.positions || []).some((p) => (p.title || '').toLowerCase().includes(q)));
  const grouped = {};
  filtered.forEach((c) => { (grouped[c.group_name] = grouped[c.group_name] || []).push(c); });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite OmniLeagues Member</DialogTitle>
          <DialogDescription>Pick an existing staff person or add someone new. They get an email link to create their account, auto-linked to the role you choose.</DialogDescription>
        </DialogHeader>

        {isOrg && mode === 'pick' ? (
          <div className="space-y-2">
            <Input placeholder="Search staff by name, email or title…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="ol-invite-candidate-search" />
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y divide-slate-700/30"
              data-testid="ol-invite-candidate-list">
              {Object.keys(grouped).length === 0 && (
                <div className="p-3 text-sm opacity-60">No staff people found for this org.</div>
              )}
              {Object.entries(grouped).map(([gname, list]) => (
                <div key={gname}>
                  <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider bg-black/5 opacity-70">{gname}</div>
                  {list.map((c) => (
                    <button key={c.coach_id} type="button" disabled={!canPick(c)} title={c.reason || ''}
                      data-testid={`ol-invite-candidate-${c.coach_id}`}
                      onClick={() => canPick(c) && choosePerson(c)}
                      className="w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-teal-600/10 disabled:opacity-40 disabled:cursor-not-allowed">
                      <span>{c.first_name} {c.last_name}
                        <span className="opacity-60"> — {c.positions?.[0]?.title}</span></span>
                      {!canPick(c)
                        ? <span className="text-[11px] text-amber-600 shrink-0">{c.reason}</span>
                        : (!c.email && <span className="text-[11px] text-slate-400 shrink-0">No email — you'll add one</span>)}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={chooseNew} data-testid="ol-invite-addnew-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Add new person
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {isOrg && (
              <Button variant="ghost" size="sm" onClick={() => setMode('pick')} data-testid="ol-invite-back-btn">
                ← Back to staff list
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First name</Label><Input value={f.first_name} onChange={set('first_name')} data-testid="ol-invite-first" /></div>
              <div><Label>Last name</Label><Input value={f.last_name} onChange={set('last_name')} data-testid="ol-invite-last" /></div>
            </div>
            <div><Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" required value={f.email} onChange={set('email')} data-testid="ol-invite-email"
                placeholder="Required — where the invite link is sent" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={set('phone')} data-testid="ol-invite-phone" /></div>
            <div><Label>Role</Label>
              <Select value={f.role} onValueChange={set('role')}>
                <SelectTrigger data-testid="ol-invite-role-select"><SelectValue placeholder="Select a role…" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{rLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {mode === 'new' && (
            <Button onClick={submit} disabled={busy || !emailValid || !f.role} data-testid="ol-member-invite-submit"
              className="bg-teal-600 hover:bg-teal-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send invite'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ scope, roles, onClose, onSaved }) {
  const [f, setF] = useState({ email: '', first_name: '', last_name: '', phone: '', role: roles[0] || '', password: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/ol-members`, { ...f, scope }, { withCredentials: true });
      toast.success('Member added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add OmniLeagues Member</DialogTitle>
          <DialogDescription>Password needs 8+ chars with an uppercase, lowercase and a number.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>First name</Label><Input value={f.first_name} onChange={set('first_name')} data-testid="ol-member-first" /></div>
            <div><Label>Last name</Label><Input value={f.last_name} onChange={set('last_name')} data-testid="ol-member-last" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={set('email')} data-testid="ol-member-email" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={set('phone')} data-testid="ol-member-phone" /></div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={set('role')}>
              <SelectTrigger data-testid="ol-member-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{rLabel(r)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Password</Label><Input type="text" value={f.password} onChange={set('password')} data-testid="ol-member-pw" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ol-member-create-submit"
            className="bg-teal-600 hover:bg-teal-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PwDialog({ scope, u, onClose, onSaved }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/ol-members/${u.id}/set-password`, { password: pw, scope }, { withCredentials: true });
      toast.success('Password set');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Set password — {u.email}</DialogTitle>
          <DialogDescription>8+ chars with an uppercase, lowercase and a number.</DialogDescription>
        </DialogHeader>
        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="ol-member-newpw-input" placeholder="New password" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !pw} data-testid="ol-member-newpw-submit"
            className="bg-teal-600 hover:bg-teal-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
